import React, { FormEvent, useEffect, useState } from 'react';
import Shell from '../../components/Shell';
import {
  CONSULAR_EQUITY_NAME,
  CONSULAR_EQUITY_SLUG,
} from '../../constants/interestOptions';
import api from '../../services/api';

type FundCard = { slug: string; name: string };

export default function InterestPage() {
  const [funds, setFunds] = useState<FundCard[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [amountHint, setAmountHint] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');

  useEffect(() => {
    api.get('/portal/funds').then((r) => setFunds(r.data));
  }, []);

  const options: FundCard[] = [
    { slug: CONSULAR_EQUITY_SLUG, name: CONSULAR_EQUITY_NAME },
    ...funds,
  ];

  function toggle(slug: string) {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selected.length) return;
    try {
      await api.post('/portal/interest', {
        fundSlugs: selected,
        amountHint: amountHint || undefined,
        notes: notes || undefined,
      });
      setStatus('ok');
      setSelected([]);
      setAmountHint('');
      setNotes('');
    } catch {
      setStatus('err');
    }
  }

  return (
    <Shell title="Soft interest">
      <h1 className="font-display text-3xl text-ink-900">Express interest</h1>
      <p className="mt-2 max-w-2xl text-sm text-ink-700">
        Non-binding. This is not a subscription or an offer to buy securities.
        We use this only to size conversations among people who already know
        Consular.
      </p>
      <form onSubmit={onSubmit} className="mt-8 max-w-xl space-y-6">
        <fieldset>
          <legend className="text-sm font-semibold text-ink-900">
            Opportunities of interest
          </legend>
          <ul className="mt-3 space-y-2">
            {options.map((f) => (
              <li key={f.slug}>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.includes(f.slug)}
                    onChange={() => toggle(f.slug)}
                    className="mt-1"
                  />
                  <span>{f.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        <label className="block text-sm">
          Approximate commitment (optional)
          <input
            value={amountHint}
            onChange={(e) => setAmountHint(e.target.value)}
            placeholder="e.g. $50k across residential"
            className="mt-1 w-full"
          />
        </label>
        <label className="block text-sm">
          Notes (optional)
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full"
          />
        </label>
        <button
          type="submit"
          disabled={!selected.length}
          className="bg-navy-900 px-5 py-2.5 text-sm font-semibold text-mist-50 hover:bg-navy-700 disabled:opacity-50"
        >
          Submit soft interest
        </button>
        {status === 'ok' && (
          <p className="text-sm text-ink-700">Received—thank you.</p>
        )}
        {status === 'err' && (
          <p className="text-sm text-red-700">Could not submit. Try again.</p>
        )}
      </form>
    </Shell>
  );
}
